import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import type { Moment } from "moment";
import { Form, Input, Checkbox, DatePicker, Tooltip } from "antd";
import type { FormInstance } from "antd";
import ModalWindow from "../../../../components/ModalWindow/ModalWindow";
import InputNumberFormat from "../../../../components/common/InputNumberFormat";
import { AnyObject } from "../../../../lib/interfaces";
import { useAppDispatch, useAppSelector } from "../../../..";
import { formatNumber } from "../../../../lib/utils";
import { AgreementsSearchModal } from "../../../../components/AgreementsSearch";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
} from "../../../../lib/const/permissions";
import { agreementsSearchActions } from "../../../../store/slices/agreementsSearch/agreementsSearch";
import { IClientType } from "../../../../lib/types";
import { ReactComponent as LoupeIcon } from "../../../../img/loupe-icon.svg";
import { limitText } from "../../../../lib/utils/limitText";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { AgreementForPaymentRebase } from "../../model/types/AgreementForPaymentRebase";
import "./PaymentTransferModal.less";

interface ComponentProps {
  maxDate: Moment | undefined;
  createPaymentTransferForm: FormInstance;
  visible: boolean;
  closeModal: () => void;
  agreementForRebase: AgreementForPaymentRebase | undefined;
  setAgreementForRebase: React.Dispatch<
    React.SetStateAction<AgreementForPaymentRebase | undefined>
  >;
  clickNextHasModal: boolean;
  setClickNextHasModal: React.Dispatch<React.SetStateAction<boolean>>;
  submitTransferPayment: (values: AnyObject) => void;
  submitTransferPaymentProcess: (values: AnyObject) => void;
  paymentTransferLimit?: {
    min: number;
    max: number;
  };
  isLoading: boolean;
}

export const PaymentTransferModal = (props: ComponentProps) => {
  const {
    maxDate,
    createPaymentTransferForm,
    closeModal,
    visible,
    agreementForRebase,
    setAgreementForRebase,
    clickNextHasModal,
    setClickNextHasModal,
    submitTransferPayment,
    submitTransferPaymentProcess,
    paymentTransferLimit = {
      max: 0,
      min: 0,
    },
    isLoading,
  } = props;

  const { payment, paymentAdjustments } = useAppSelector((state) => state.accountPaymentDetails);

  const dispatch = useAppDispatch();

  const [searchClientType, setSearchClientType] = useState<IClientType>("ORGANIZATION");

  const hasAccessToOrganization = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  const agreementTitle = useMemo(() => {
    if (!agreementForRebase) return "";
    return `${agreementForRebase.agreementNumber} / ${agreementForRebase.fullName}`;
  }, [agreementForRebase]);

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setSearchClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setSearchClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  const toggleSearchClientType = () => {
    if (allowedClientChange) {
      setSearchClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
      dispatch(agreementsSearchActions.cleanAccounts());
    }
  };

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const onCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const onSubmitSearchModal = (
    v,
    agreement?: {
      agreementId: number;
      agreementNumber: string;
      accountId: number;
      fullName: string;
    }[]
  ) => {
    onCloseSearchModal();
    if (agreement) {
      setAgreementForRebase(agreement[0]);
      setIsSearchModalOpen(false);
    }
  };

  // Очистка списка accountSelect при входе и выходе
  useEffect(() => {
    dispatch(agreementsSearchActions.cleanAccounts());
    return () => {
      dispatch(agreementsSearchActions.cleanAccounts());
    };
  }, []);

  const closeTransferPaymentModal = () => {
    createPaymentTransferForm.resetFields();
    closeModal();
    setAgreementForRebase(undefined);
  };

  const submitPaymentTransferModal = () => {
    createPaymentTransferForm.validateFields().then(() => {
      createPaymentTransferForm.submit();
      setClickNextHasModal(true);
    });
  };

  const onSubmitFormatProcess = (values) => {
    createPaymentTransferForm.getFieldValue("transferPayment")
      ? submitTransferPayment(values)
      : submitTransferPaymentProcess(values);
  };

  const isAdjustmentStatusCorrectForTrunsfer = paymentAdjustments
    ?.filter(({ parentTransactionId }) => parentTransactionId === payment?.transactionId)
    .some(({ adjustmentTypeId }) => adjustmentTypeId === 4 || adjustmentTypeId === 2);

  const getValidDatePaymentTransfer = () => {
    if (maxDate) {
      return moment().isBefore(maxDate) ? maxDate.clone().add(1, "days") : moment();
    }
  };

  return (
    <>
      <ModalWindow
        visible={visible}
        title={
          payment?.paymentStatusId === 2
            ? "Вы действительно хотите перенести платеж?"
            : "Перенос платежа на другой договор"
        }
        handleSubmit={submitPaymentTransferModal}
        handleCancel={closeTransferPaymentModal}
        handleReset={closeTransferPaymentModal}
        cancelTextButton="Отмена"
        submitTextButton="Далее"
        width={750}
        disabled={!agreementForRebase?.agreementNumber || clickNextHasModal}
        submitTooltip={
          agreementForRebase?.agreementNumber ? "" : "Необходимо выбрать договор для переноса"
        }
        className="payment-transfer-modal"
        submitButtonLoading={isLoading}
      >
        <Form
          form={createPaymentTransferForm}
          onFinish={onSubmitFormatProcess}
          labelCol={{ span: 10 }}
          initialValues={{ transferDate: getValidDatePaymentTransfer() }}
        >
          <Form.Item
            className="payment-transfer-modal__agreement"
            // name="agreementId"
            label={
              // <Button onClick={() => setIsSearchModalOpen(true)} style={{ width: "275px" }}>
              //   {agreementForRebase?.agreementNumber
              //     ? "Выбрать другой договор"
              //     : "Выбрать договор для переноса платежа"}
              // </Button>
              <div>
                Клиент
                <Tooltip title="Открыть расширенный поиск">
                  <LoupeIcon
                    onClick={() => {
                      setIsSearchModalOpen(true);
                    }}
                  />
                </Tooltip>
              </div>
            }
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Tooltip title={agreementTitle} placement="topLeft">
              <div>
                <Input
                  placeholder="Договор не выбран"
                  disabled
                  value={limitText(agreementTitle, 50)}
                />
              </div>
            </Tooltip>
          </Form.Item>
          <Form.Item name="transferPayment" label="Перенос всей суммы" valuePropName="checked">
            <Checkbox
              disabled={
                isAdjustmentStatusCorrectForTrunsfer ||
                Number(payment?.extra?.availableAmount) === 0
              }
            >
              При полном переносе все отметки оплаты будут удалены
            </Checkbox>
          </Form.Item>

          <Form.Item
            dependencies={["transferPayment"]}
            shouldUpdate
            className="form-item-required__hidden-error-margin"
          >
            {({ getFieldValue }) => {
              const transferPayment = getFieldValue("transferPayment");
              return (
                <Form.Item
                  name="transferAmount"
                  label="Сумма переноса"
                  rules={[
                    {
                      required: !transferPayment,
                      message: "Поле обязательно для заполнения",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        if (
                          Number(value) < paymentTransferLimit.min ||
                          Number(value) > paymentTransferLimit.max
                        ) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      },
                      message: "Недостаточно средств для переноса",
                    },
                  ]}
                  labelCol={{ span: 10 }}
                  dependencies={["transferPayment"]}
                >
                  <InputNumberFormat
                    style={{ width: "150px" }}
                    placeholder={`${formatNumber(paymentTransferLimit.min)} - ${formatNumber(
                      paymentTransferLimit.max
                    )}`}
                    allowNegative={false}
                    allowClear
                    disabled={transferPayment}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            dependencies={["transferPayment"]}
            shouldUpdate
            className="form-item-required__hidden-error__mb0"
          >
            {({ getFieldValue }) => {
              const transferPayment = getFieldValue("transferPayment");
              return (
                <Form.Item
                  name="transferDate"
                  label="Дата переноса"
                  labelCol={{ span: 10 }}
                  dependencies={["transferPayment"]}
                >
                  <DatePicker
                    style={{ width: "150px" }}
                    disabled={transferPayment}
                    disabledDate={(date) =>
                      date.isBefore(maxDate, "day") || date.isAfter(moment().endOf("day"), "day")
                    }
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            name="reason"
            label="Основание действия"
            className="form-item-required__hidden-error__mb0"
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </ModalWindow>
      <AgreementsSearchModal
        clientType={searchClientType}
        toggleClientType={toggleSearchClientType}
        visible={isSearchModalOpen}
        onClose={onCloseSearchModal}
        onSubmit={onSubmitSearchModal}
        okText="Выбрать договор"
      />
    </>
  );
};
